import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { brl, fmtDateBR, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { Sparkles, Check, Clock, Instagram, MapPin } from "lucide-react";

export default function BookingPublic() {
  const { slug } = useParams();
  const [studio, setStudio] = useState(null);
  const [services, setServices] = useState([]);
  const [step, setStep] = useState(1);
  const [selected, setSelected] = useState(null);
  const [date, setDate] = useState(todayISO());
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [time, setTime] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [confirmation, setConfirmation] = useState(null);
  const [deposit, setDeposit] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/public/studio/${slug}`).then(r => { setStudio(r.data.studio); setServices(r.data.services); })
      .catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (step === 3 && date && selected?.id) {
      setLoadingSlots(true);
      setSlots([]);
      api.get(`/public/studio/${slug}/availability`, {
        params: { date, service_id: selected.id },
      })
        .then(r => setSlots(r.data.slots || []))
        .catch(e => {
          setSlots([]);
          toast.error(apiError(e, "Não foi possível carregar os horários."));
        })
        .finally(() => setLoadingSlots(false));
    }
  }, [step, date, slug, selected?.id]);

  const book = async () => {
    try {
      const r = await api.post(`/public/studio/${slug}/book`, {
        service_id: selected.id, date, time,
        client_name: form.name, client_phone: form.phone, client_email: form.email,
      });
      setConfirmation(r.data.confirmation_code);
      setDeposit(r.data.deposit);
      setStep(5);
    } catch (e) { toast.error(apiError(e)); }
  };

  if (notFound) return <div className="min-h-screen flex items-center justify-center text-[#71717A]">Studio não encontrado.</div>;
  if (!studio) return <div className="min-h-screen flex items-center justify-center text-[#71717A]">Carregando…</div>;

  return (
    <div className="min-h-screen grain">
      <header className="bg-[#682F3E] text-white py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            {studio.logo_url ? (
              <img src={studio.logo_url} alt="logo" className="w-14 h-14 rounded-xl object-cover border-2 border-[#EBD8D2]" />
            ) : (
              <div className="w-14 h-14 rounded-xl bg-[#EBD8D2] flex items-center justify-center text-[#682F3E] font-display font-bold text-xl">
                {studio.name.slice(0, 1).toUpperCase()}
              </div>
            )}
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <Sparkles size={12} className="text-[#EBD8D2]" />
                <span className="text-[10px] uppercase font-semibold tracking-wider text-[#EBD8D2]">NailControl</span>
              </div>
              <h1 className="font-display text-2xl md:text-4xl font-bold leading-tight">{studio.name}</h1>
            </div>
          </div>
          {studio.bio && <p className="text-[#EBD8D2] mt-2 text-sm">{studio.bio}</p>}
          <div className="flex flex-wrap gap-3 mt-3 text-xs text-[#EBD8D2]">
            {studio.instagram && <span className="flex items-center gap-1"><Instagram size={14} /> @{studio.instagram}</span>}
            {studio.address && <span className="flex items-center gap-1"><MapPin size={14} /> {studio.address}</span>}
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        <div className="card-nc p-5 space-y-4">
          {step === 1 && (
            <>
              <h2 className="font-display text-xl font-bold text-[#27272A]">Escolha um serviço</h2>
              {services.length === 0 && <p className="text-[#71717A]">Nenhum serviço disponível.</p>}
              {services.map(s => (
                <button key={s.id} data-testid={`pub-svc-${s.id}`} onClick={() => { setSelected(s); setStep(2); }}
                  className="w-full text-left p-4 rounded-xl border border-[#E4E4E7] hover:border-[#C98F96] flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-[#27272A]">{s.name}</div>
                    <div className="text-xs text-[#71717A] flex items-center gap-2 mt-0.5"><Clock size={12} /> {s.duration_minutes} min</div>
                  </div>
                  <div className="font-mono-brand font-bold text-[#682F3E]">{brl(s.price)}</div>
                </button>
              ))}
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="font-display text-xl font-bold text-[#27272A]">Escolha a data</h2>
              <input data-testid="pub-date" type="date" min={todayISO()} value={date} onChange={e => { setDate(e.target.value); setTime(""); }} className="input-nc" />
              <div className="flex justify-between">
                <button onClick={() => setStep(1)} className="text-[#71717A] text-sm">Voltar</button>
                <button data-testid="pub-next-date" onClick={() => setStep(3)} className="btn-primary-nc text-sm">Continuar</button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h2 className="font-display text-xl font-bold text-[#27272A]">Escolha o horário</h2>
              <p className="text-sm text-[#71717A]">{fmtDateBR(date)}</p>
              {loadingSlots ? (
                <div className="p-4 rounded-xl bg-[#FAF8F6] text-sm text-[#71717A] text-center">
                  Carregando horários disponíveis…
                </div>
              ) : slots.length === 0 ? (
                <div className="p-4 rounded-xl bg-[#F5ECE8] text-sm text-[#682F3E] text-center">
                  Não há atendimento disponível nesta data. Escolha outro dia.
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-4 md:grid-cols-6 gap-2 max-h-64 overflow-y-auto">
                    {slots.map(s => (
                      <button key={s.time} disabled={!s.available} data-testid={`pub-slot-${s.time}`}
                        onClick={() => { setTime(s.time); setStep(4); }}
                        className={`p-2 rounded-lg text-sm font-semibold ${s.available ? (time === s.time ? "bg-[#682F3E] text-white" : "bg-white border border-[#E4E4E7] text-[#27272A]") : "bg-[#F5ECE8] text-[#C98F96] cursor-not-allowed"}`}>
                        {s.time}
                      </button>
                    ))}
                  </div>
                  {!slots.some(s => s.available) && (
                    <div className="p-3 rounded-xl bg-[#F5ECE8] text-sm text-[#682F3E] text-center">
                      Todos os horários desta data já estão ocupados.
                    </div>
                  )}
                </>
              )}
              <button onClick={() => setStep(2)} className="text-[#71717A] text-sm">Voltar</button>
            </>
          )}

          {step === 4 && (
            <>
              <h2 className="font-display text-xl font-bold text-[#27272A]">Seus dados</h2>
              <div className="p-3 rounded-xl bg-[#F5ECE8] text-sm">
                <b>{selected?.name}</b> · {fmtDateBR(date)} às {time} · {brl(selected?.price)}
              </div>
              <input data-testid="pub-name" placeholder="Seu nome" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="input-nc" />
              <input data-testid="pub-phone" placeholder="WhatsApp (com DDD)" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="input-nc" />
              <input data-testid="pub-email" placeholder="E-mail (opcional)" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="input-nc" />
              <button data-testid="pub-confirm" onClick={book} disabled={!form.name || !form.phone} className="btn-primary-nc w-full">
                Confirmar agendamento
              </button>
            </>
          )}

          {step === 5 && (
            <div className="text-center py-6" data-testid="pub-success">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#EBF2EE] flex items-center justify-center mb-3">
                <Check size={32} className="text-[#4F7A61]" />
              </div>
              <h2 className="font-display text-2xl font-bold text-[#27272A]">Agendamento confirmado!</h2>
              <p className="text-sm text-[#71717A] mt-2">Código: <b className="font-mono-brand">{confirmation}</b></p>
              <p className="text-sm text-[#27272A] mt-2">{fmtDateBR(date)} às {time}</p>

              {deposit?.required && (
                <div className="mt-5 p-4 rounded-2xl bg-[#F5ECE8] border-2 border-dashed border-[#682F3E]" data-testid="deposit-block">
                  <div className="text-xs uppercase font-bold tracking-wider text-[#682F3E]">Sinal de reserva</div>
                  <div className="font-mono-brand text-3xl font-bold text-[#682F3E] mt-1">{brl(deposit.amount)}</div>
                  <p className="text-xs text-[#71717A] mt-1">
                    Para garantir seu horário, faça o Pix do sinal com a chave abaixo. O restante você paga no dia.
                  </p>
                  {deposit.pix_key && (
                    <div className="mt-3 space-y-2 text-left">
                      <div>
                        <div className="text-[10px] uppercase font-semibold text-[#71717A]">Tipo</div>
                        <div className="font-semibold text-sm text-[#27272A] capitalize">{deposit.pix_key_type}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase font-semibold text-[#71717A]">Chave Pix</div>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 font-mono-brand text-sm text-[#27272A] bg-white px-3 py-2 rounded-lg break-all">{deposit.pix_key}</code>
                          <button data-testid="copy-pix"
                            onClick={async () => { try { await navigator.clipboard.writeText(deposit.pix_key); toast.success("Chave copiada!"); } catch { toast.error("Não foi possível copiar."); } }}
                            className="btn-primary-nc py-2 px-3 text-xs">Copiar</button>
                        </div>
                      </div>
                      <p className="text-[10px] text-[#71717A]">Após o pagamento, envie o comprovante no WhatsApp para confirmação.</p>
                    </div>
                  )}
                </div>
              )}
              <p className="text-xs text-[#71717A] mt-4">Guarde este código para consultar seu agendamento.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
